import { formatCents, type Cents } from "../money";

export type KeypadSign = "outflow" | "inflow";

export type KeypadDigit = "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "00";

export interface KeypadState {
  /** The typed digits with no leading zeros; the last two are cents. Empty means zero. */
  digits: string;
  sign: KeypadSign;
}

export type KeypadAction =
  | { type: "digit"; digit: KeypadDigit }
  | { type: "backspace" }
  | { type: "toggleSign" }
  | { type: "clear" }
  | { type: "set"; cents: Cents };

/** $999,999,999.99 */
export const keypadMaxDigits = 11;

export function initialKeypadState(sign: KeypadSign = "outflow"): KeypadState {
  return { digits: "", sign };
}

export function keypadReducer(state: KeypadState, action: KeypadAction): KeypadState {
  switch (action.type) {
    case "digit": {
      let digits = state.digits;
      for (const d of action.digit) {
        digits = appendDigit(digits, d);
      }
      return digits === state.digits ? state : { ...state, digits };
    }
    case "backspace":
      return state.digits === "" ? state : { ...state, digits: state.digits.slice(0, -1) };
    case "toggleSign":
      return { ...state, sign: state.sign === "outflow" ? "inflow" : "outflow" };
    case "clear":
      return { ...state, digits: "" };
    case "set":
      return setCents(state, action.cents);
  }
}

function appendDigit(digits: string, d: string): string {
  if (digits === "" && d === "0") {
    return digits;
  }
  if (digits.length >= keypadMaxDigits) {
    return digits;
  }
  return digits + d;
}

/** A zero amount keeps the current sign, so loading an empty amount doesn't flip the toggle. */
function setCents(state: KeypadState, cents: Cents): KeypadState {
  const abs = Math.min(Math.abs(Math.trunc(cents)), 10 ** keypadMaxDigits - 1);
  const digits = abs === 0 ? "" : String(abs);
  if (cents === 0) {
    return { ...state, digits };
  }
  return { digits, sign: cents < 0 ? "outflow" : "inflow" };
}

function unsignedCents(state: KeypadState): Cents {
  return state.digits === "" ? 0 : Number(state.digits);
}

/** Signed cents: outflow is negative. */
export function keypadCents(state: KeypadState): Cents {
  const abs = unsignedCents(state);
  return state.sign === "outflow" && abs !== 0 ? -abs : abs;
}

/** The unsigned amount, formatted as typed (`$4.00`). The UI shows the sign separately. */
export function keypadDisplay(state: KeypadState): string {
  return formatCents(unsignedCents(state));
}
